import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Truck, 
  ShoppingCart, 
  Package,
  Check, 
  X, 
  Edit, 
  Trash2,
  Building2,
  Phone,
  Mail,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { suppliersAPI, purchaseOrdersAPI, materialsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  partial: 'bg-blue-100 text-blue-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  partial: 'Parcial',
  received: 'Recibida',
  cancelled: 'Cancelada'
};

export default function PurchasesPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [expandedOrder, setExpandedOrder] = useState(null);
  
  // Supplier form
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    name: '', rnc: '', contact_name: '', address: '', phone: '', email: '', notes: ''
  });
  
  // Purchase order form
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({
    supplier_id: '',
    expected_date: '',
    notes: '',
    items: []
  });
  const [newItem, setNewItem] = useState({
    material_id: '', quantity: 0, unit: 'qq', unit_cost: 0, currency: 'DOP'
  });
  
  // Delete dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Receive/Cancel dialogs
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [suppliersRes, ordersRes, materialsRes] = await Promise.all([
        suppliersAPI.getAll(),
        purchaseOrdersAPI.getAll(),
        materialsAPI.getAll()
      ]);
      setSuppliers(suppliersRes.data);
      setOrders(ordersRes.data);
      setMaterials(materialsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Supplier handlers
  const resetSupplierForm = () => {
    setEditingSupplier(null);
    setSupplierForm({ name: '', rnc: '', contact_name: '', address: '', phone: '', email: '', notes: '' });
    setSupplierDialogOpen(false);
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name,
      rnc: supplier.rnc || '',
      contact_name: supplier.contact_name || '',
      address: supplier.address || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      notes: supplier.notes || ''
    });
    setSupplierDialogOpen(true);
  };

  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await suppliersAPI.update(editingSupplier.id, supplierForm);
        toast.success('Proveedor actualizado');
      } else {
        await suppliersAPI.create(supplierForm);
        toast.success('Proveedor creado');
      }
      resetSupplierForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    }
  };

  const handleDeleteSupplier = (supplier) => {
    setDeleteType('supplier');
    setItemToDelete(supplier);
    setDeleteDialogOpen(true);
  };

  // Order handlers
  const resetOrderForm = () => {
    setOrderForm({ supplier_id: '', expected_date: '', notes: '', items: [] });
    setNewItem({ material_id: '', quantity: 0, unit: 'qq', unit_cost: 0, currency: 'DOP' });
    setOrderDialogOpen(false);
  };

  const handleAddItem = () => {
    if (!newItem.material_id || newItem.quantity <= 0 || newItem.unit_cost <= 0) {
      toast.error('Completa todos los campos del item');
      return;
    }
    
    const material = materials.find(m => m.id === newItem.material_id);
    if (!material) return;
    
    if (orderForm.items.some(i => i.material_id === newItem.material_id)) {
      toast.error('Este material ya está en la orden');
      return;
    }
    
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, { ...newItem, material_name: material.name }]
    });
    setNewItem({ material_id: '', quantity: 0, unit: 'qq', unit_cost: 0, currency: orderForm.items[0]?.currency || 'DOP' });
  };

  const handleRemoveItem = (materialId) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter(i => i.material_id !== materialId)
    });
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.supplier_id) {
      toast.error('Selecciona un proveedor');
      return;
    }
    if (orderForm.items.length === 0) {
      toast.error('Agrega al menos un item');
      return;
    }
    
    try {
      await purchaseOrdersAPI.create(orderForm);
      toast.success('Orden de compra creada');
      resetOrderForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear orden');
    }
  };

  const handleReceiveOrder = async () => {
    if (!selectedOrder) return;
    try {
      await purchaseOrdersAPI.receive(selectedOrder.id);
      toast.success('Orden recibida. Inventario actualizado.');
      setReceiveDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recibir orden');
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    try {
      await purchaseOrdersAPI.cancel(selectedOrder.id);
      toast.success('Orden cancelada');
      setCancelDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar orden');
    }
  };

  const handleDeleteOrder = (order) => {
    setDeleteType('order');
    setItemToDelete(order);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      if (deleteType === 'supplier') {
        await suppliersAPI.delete(itemToDelete.id);
        toast.success('Proveedor eliminado');
      } else if (deleteType === 'order') {
        await purchaseOrdersAPI.delete(itemToDelete.id);
        toast.success('Orden eliminada');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteType(null);
    }
  };

  const orderTotal = orderForm.items.reduce((sum, i) => sum + (i.quantity * i.unit_cost), 0);
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const receivedOrders = orders.filter(o => o.status === 'received').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Compras</h1>
          <p className="text-slate-500 mt-1">Gestión de proveedores y órdenes de compra</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Proveedores</p>
              <p className="text-xl font-bold text-slate-900">{suppliers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Órdenes Pendientes</p>
              <p className="text-xl font-bold text-slate-900">{pendingOrders}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Órdenes Recibidas</p>
              <p className="text-xl font-bold text-slate-900">{receivedOrders}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Órdenes</p>
              <p className="text-xl font-bold text-slate-900">{orders.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Órdenes de Compra
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="flex items-center gap-2">
            <Truck className="h-4 w-4" />
            Proveedores
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={orderDialogOpen} onOpenChange={(open) => {
              setOrderDialogOpen(open);
              if (!open) resetOrderForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-order-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Orden de Compra
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nueva Orden de Compra</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateOrder} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Proveedor *</Label>
                      <Select 
                        value={orderForm.supplier_id} 
                        onValueChange={(v) => setOrderForm({...orderForm, supplier_id: v})}
                      >
                        <SelectTrigger data-testid="order-supplier-select">
                          <SelectValue placeholder="Seleccionar proveedor" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha Esperada</Label>
                      <Input
                        type="date"
                        value={orderForm.expected_date}
                        onChange={(e) => setOrderForm({...orderForm, expected_date: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Add Items */}
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold">Items de la Orden</Label>
                    <div className="mt-3 grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-3">
                        <Label className="text-xs">Material</Label>
                        <Select 
                          value={newItem.material_id} 
                          onValueChange={(v) => setNewItem({...newItem, material_id: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {materials.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 0})}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Unidad</Label>
                        <Select 
                          value={newItem.unit} 
                          onValueChange={(v) => setNewItem({...newItem, unit: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="qq">Quintales (qq)</SelectItem>
                            <SelectItem value="lb">Libras (lb)</SelectItem>
                            <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                            <SelectItem value="ton">Toneladas</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Precio Unit.</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newItem.unit_cost}
                          onChange={(e) => setNewItem({...newItem, unit_cost: parseFloat(e.target.value) || 0})}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Moneda</Label>
                        <Select 
                          value={newItem.currency} 
                          onValueChange={(v) => setNewItem({...newItem, currency: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DOP">RD$</SelectItem>
                            <SelectItem value="USD">US$</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Button 
                          type="button" 
                          onClick={handleAddItem}
                          className="w-full bg-[#E97171] hover:bg-[#931A25]"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Items List */}
                    {orderForm.items.length > 0 && (
                      <div className="mt-4 border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead>Material</TableHead>
                              <TableHead className="text-right">Cantidad</TableHead>
                              <TableHead className="text-right">Precio Unit.</TableHead>
                              <TableHead className="text-right">Subtotal</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {orderForm.items.map((item) => (
                              <TableRow key={item.material_id}>
                                <TableCell>{item.material_name}</TableCell>
                                <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                                <TableCell className="text-right">{formatCurrency(item.unit_cost, item.currency)}</TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(item.quantity * item.unit_cost, item.currency)}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveItem(item.material_id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-slate-50 font-semibold">
                              <TableCell colSpan={3}>Total</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(orderTotal, orderForm.items[0]?.currency || 'DOP')}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Notas</Label>
                    <Textarea
                      value={orderForm.notes}
                      onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                      placeholder="Notas adicionales..."
                      rows={2}
                    />
                  </div>

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" variant="outline">Cancelar</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">
                      Crear Orden
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Orders List */}
          {orders.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center text-slate-500">
                No hay órdenes de compra. Crea una nueva orden para comenzar.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="border-slate-200" data-testid={`order-card-${order.id}`}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-slate-50 transition-colors py-4"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <ShoppingCart className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg">{order.order_number}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status]}`}>
                              {STATUS_LABELS[order.status]}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">{order.supplier_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Total</p>
                          <p className="font-semibold text-[#931A25]">
                            {formatCurrency(order.total_amount, order.currency)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Fecha</p>
                          <p className="text-sm">{formatDate(order.created_at)}</p>
                        </div>
                        {expandedOrder === order.id ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedOrder === order.id && (
                    <CardContent className="border-t pt-4">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Material</TableHead>
                            <TableHead className="text-right">Cantidad</TableHead>
                            <TableHead className="text-right">Precio Unit.</TableHead>
                            <TableHead className="text-right">Subtotal</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.material_name}</TableCell>
                              <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.unit_cost, item.currency)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.quantity * item.unit_cost, item.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {order.notes && (
                        <p className="mt-4 text-sm text-slate-600">
                          <strong>Notas:</strong> {order.notes}
                        </p>
                      )}
                      
                      <div className="mt-4 flex justify-end gap-2">
                        {order.status === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedOrder(order);
                                setReceiveDialogOpen(true);
                              }}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Recibir
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedOrder(order);
                                setCancelDialogOpen(true);
                              }}
                              className="text-orange-600 border-orange-200 hover:bg-orange-50"
                            >
                              <X className="h-4 w-4 mr-2" />
                              Cancelar
                            </Button>
                          </>
                        )}
                        {order.status !== 'received' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteOrder(order)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={supplierDialogOpen} onOpenChange={(open) => {
              setSupplierDialogOpen(open);
              if (!open) resetSupplierForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-supplier-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Proveedor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveSupplier} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre *</Label>
                      <Input
                        value={supplierForm.name}
                        onChange={(e) => setSupplierForm({...supplierForm, name: e.target.value})}
                        placeholder="Nombre de la empresa"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>RNC</Label>
                      <Input
                        value={supplierForm.rnc}
                        onChange={(e) => setSupplierForm({...supplierForm, rnc: e.target.value})}
                        placeholder="000-00000-0"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Persona de Contacto</Label>
                    <Input
                      value={supplierForm.contact_name}
                      onChange={(e) => setSupplierForm({...supplierForm, contact_name: e.target.value})}
                      placeholder="Nombre del contacto"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input
                        value={supplierForm.phone}
                        onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})}
                        placeholder="809-000-0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={supplierForm.email}
                        onChange={(e) => setSupplierForm({...supplierForm, email: e.target.value})}
                        placeholder="correo@empresa.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Dirección</Label>
                    <Input
                      value={supplierForm.address}
                      onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})}
                      placeholder="Dirección completa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notas</Label>
                    <Textarea
                      value={supplierForm.notes}
                      onChange={(e) => setSupplierForm({...supplierForm, notes: e.target.value})}
                      placeholder="Notas adicionales..."
                      rows={2}
                    />
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" variant="outline">Cancelar</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">
                      {editingSupplier ? 'Actualizar' : 'Guardar'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Suppliers Table */}
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Proveedor</TableHead>
                    <TableHead>RNC</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hay proveedores registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    suppliers.map((supplier) => (
                      <TableRow key={supplier.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{supplier.name}</TableCell>
                        <TableCell>{supplier.rnc || '-'}</TableCell>
                        <TableCell>{supplier.contact_name || '-'}</TableCell>
                        <TableCell>
                          {supplier.phone && (
                            <span className="flex items-center gap-1 text-sm">
                              <Phone className="h-3 w-3" />
                              {supplier.phone}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {supplier.email && (
                            <span className="flex items-center gap-1 text-sm">
                              <Mail className="h-3 w-3" />
                              {supplier.email}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditSupplier(supplier)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteSupplier(supplier)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === 'supplier' 
                ? `Se eliminará el proveedor "${itemToDelete?.name}". Esta acción no se puede deshacer.`
                : `Se eliminará la orden "${itemToDelete?.order_number}". Esta acción no se puede deshacer.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receive Order Dialog */}
      <AlertDialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Recibir orden de compra?</AlertDialogTitle>
            <AlertDialogDescription>
              Al recibir la orden {selectedOrder?.order_number}, todos los materiales se agregarán 
              automáticamente al inventario con los costos especificados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReceiveOrder} className="bg-green-600 hover:bg-green-700">
              Recibir Orden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Order Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar orden de compra?</AlertDialogTitle>
            <AlertDialogDescription>
              La orden {selectedOrder?.order_number} será marcada como cancelada. 
              No se afectará el inventario.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelOrder} className="bg-orange-600 hover:bg-orange-700">
              Cancelar Orden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
